import ChatIcon from "@mui/icons-material/Chat";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ScienceIcon from "@mui/icons-material/Science";
import { Alert, Box, Drawer, Fab, Paper, Snackbar, Tooltip } from "@mui/material";
import { DataGridPro as DataGrid, GridRowSelectionModel } from "@mui/x-data-grid-pro";
import React from "react";

import { FeatureFlag } from "../../common/posthog";
import { Artifact } from "../../types/artifact";
import { Viewer } from "../viewer/Viewer";
import ChatSidebar from "./components/ChatSidebar";
import ExploreHeader from "./components/ExploreHeader";
import FindBestWorstCallsWizard from "./components/find-best-worst/FindBestWorstCallsWizard";
import LensTester from "./components/LensTester";
import RankedCallsEmptyOverlay from "./components/RankedCallsEmptyOverlay";
import { DRAWER_Z_INDEX_OFFSET, INITIAL_INDEX, SINGLE_SELECTION } from "./constants/numeric";
import type { NotificationState } from "./hooks/notification";
import { useExploreAutoOpen } from "./hooks/useExploreAutoOpen";
import { useExploreClipboard } from "./hooks/useExploreClipboard";
import { useExploreNavigation, type UseExploreNavigationResult } from "./hooks/useExploreNavigation";
import {
  type ExploreData,
  type ExploreGrid,
  getRowKey,
  useExploreState
} from "./hooks/useExploreState";
import type { UseRankedCallsModeResult } from "./hooks/useRankedCallsMode";
import { RowSelectionMode } from "./stores/chatTypes";

const SNACKBAR_DURATION = 5000;
const PAGE_SIZE_SMALL = 100;
const PAGE_SIZE_MEDIUM = 200;
const PAGE_SIZE_LARGE = 500;
const PAGE_SIZE_XLARGE = 1000;
const PAGE_SIZE_OPTIONS = [PAGE_SIZE_SMALL, PAGE_SIZE_MEDIUM, PAGE_SIZE_LARGE, PAGE_SIZE_XLARGE];

interface FloatingActionsProps {
  rowSelectionModel: GridRowSelectionModel;
  isJsonCopying: boolean;
  isRagEnabled: boolean;
  onOpenLensTester: () => void;
  onCopyJson: () => void;
  onToggleChat: () => void;
}

const LENS_TESTER_FAB_SX = { position: "absolute", bottom: 219, right: 24, zIndex: 10 } as const;
const COPY_JSON_FAB_SX = { position: "absolute", bottom: 147, right: 24, zIndex: 10 } as const;
const CHAT_FAB_SX = { position: "absolute", bottom: 75, right: 24, zIndex: 10 } as const;

const FloatingActions = ({
  rowSelectionModel, isJsonCopying, isRagEnabled, onOpenLensTester, onCopyJson, onToggleChat
}: FloatingActionsProps): React.ReactElement => (
  <>
    {rowSelectionModel.length === SINGLE_SELECTION && (
      <FeatureFlag flag="conversation-lens-tester" fallback={<></>}>
        <Tooltip title="Test lens with selected interaction" placement="left">
          <Fab color="info" aria-label="lens tester" onClick={onOpenLensTester} sx={LENS_TESTER_FAB_SX}>
            <ScienceIcon />
          </Fab>
        </Tooltip>
      </FeatureFlag>
    )}
    {rowSelectionModel.length > INITIAL_INDEX && (
      <FeatureFlag flag="copy-datasets" fallback={<></>}>
        <Tooltip
          title={`Copy ${rowSelectionModel.length} selected ${rowSelectionModel.length === SINGLE_SELECTION ? "interaction" : "interactions"} as JSON`}
          placement="left"
        >
          <Fab color="secondary" aria-label="copy as json" onClick={onCopyJson} disabled={isJsonCopying} sx={COPY_JSON_FAB_SX}>
            <FileDownloadIcon />
          </Fab>
        </Tooltip>
      </FeatureFlag>
    )}
    {isRagEnabled && (
      <Tooltip title="Ask about this data" placement="left">
        <Fab color="primary" aria-label="chat" onClick={onToggleChat} sx={CHAT_FAB_SX}>
          <ChatIcon />
        </Fab>
      </Tooltip>
    )}
  </>
);

interface ViewerDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  selectedInteractionId: string | null;
  viewerInitialTab: number;
  expandedAttribute: string | null;
  currentRowIndex: number;
  totalCount: number;
  onNavigatePrevious: () => void;
  onNavigateNext: () => void;
}

const VIEWER_DRAWER_SX = {
  width: "75%",
  flexShrink: 0,
  ["& .MuiDrawer-paper"]: {
    width: "75%",
    boxSizing: "border-box",
    zIndex: (theme: { zIndex: { drawer: number } }) => theme.zIndex.drawer + DRAWER_Z_INDEX_OFFSET,
    transition: "transform 0.3s ease-in-out"
  }
} as const;
const VIEWER_TRANSITION_DURATION = 300;

const ViewerDrawer = ({
  isOpen, onClose, selectedInteractionId, viewerInitialTab,
  expandedAttribute, currentRowIndex, totalCount, onNavigatePrevious, onNavigateNext
}: ViewerDrawerProps): React.ReactElement => (
  <Drawer
    anchor="right"
    open={isOpen}
    onClose={onClose}
    sx={VIEWER_DRAWER_SX}
    transitionDuration={VIEWER_TRANSITION_DURATION}
  >
    <Box sx={{ mt: 8 }}>
      {selectedInteractionId && (
        <Viewer
          key={selectedInteractionId}
          interactionId={selectedInteractionId}
          initialTab={viewerInitialTab}
          expandedAttribute={expandedAttribute || undefined}
          onClose={onClose}
          navigation={{
            currentIndex: currentRowIndex,
            totalCount,
            onPrevious: onNavigatePrevious,
            onNext: onNavigateNext
          }}
        />
      )}
    </Box>
  </Drawer>
);

const getGridHeight = (isRankedActive: boolean, isFilterExpanded: boolean): string => {
  if (isRankedActive) {
    return "calc(100vh - 280px)";
  }
  return isFilterExpanded ? "calc(100vh - 390px)" : "calc(100vh - 240px)";
};

interface ExploreDataGridActions {
  onCellClick: (params: import("@mui/x-data-grid-pro").GridCellParams) => void;
  onReopenWizard: () => void;
  onOpenLensTester: () => void;
  onCopyJson: () => void;
  onToggleChat: () => void;
}

interface ExploreDataGridProps {
  rankedCalls: UseRankedCallsModeResult;
  data: ExploreData;
  grid: ExploreGrid;
  isFilterBarExpanded: boolean;
  isJsonCopying: boolean;
  isRagEnabled: boolean;
  actions: ExploreDataGridActions;
}

const buildDataGridProps = (props: ExploreDataGridProps): React.ComponentProps<typeof DataGrid> => {
  const { rankedCalls, data, grid, actions } = props;
  const isActive = rankedCalls.isActive;
  return {
    getRowId: (row) => `${row.interaction_id}-${row.created}`,
    apiRef: grid.apiRef,
    sx: { overflowY: "hidden" },
    rows: data.rows,
    columns: grid.columns,
    rowCount: data.rowCount,
    loading: data.loading,
    pageSizeOptions: isActive ? [grid.effectivePageSize] : PAGE_SIZE_OPTIONS,
    pagination: true,
    paginationModel: isActive ? { page: INITIAL_INDEX, pageSize: grid.effectivePageSize } : grid.paginationModel,
    paginationMode: isActive ? "client" : "server",
    paginationMeta: isActive ? undefined : { hasNextPage: true },
    sortingMode: isActive ? "client" : "server",
    sortModel: grid.effectiveSortModel,
    onPaginationModelChange: isActive ? undefined : grid.setPaginationModel,
    onSortModelChange: isActive ? grid.setRankedCallsSortModel : grid.setSortModel,
    disableRowSelectionOnClick: true,
    columnVisibilityModel: grid.columnVisibilityModel,
    onColumnVisibilityModelChange: grid.setColumnVisibilityModel,
    onCellClick: actions.onCellClick,
    checkboxSelection: true,
    checkboxSelectionVisibleOnly: false,
    rowSelectionModel: grid.rowSelectionModel,
    onRowSelectionModelChange: grid.setRowSelectionModel,
    slots: isActive
      ? { noRowsOverlay: () => <RankedCallsEmptyOverlay onReopenWizard={actions.onReopenWizard} /> }
      : undefined
  };
};

const ExploreDataGrid = (props: ExploreDataGridProps): React.ReactElement => {
  const { rankedCalls, grid, isFilterBarExpanded, isJsonCopying, isRagEnabled, actions } = props;
  const gridDimension = getGridHeight(rankedCalls.isActive, isFilterBarExpanded);
  const paperSx = {
    height: gridDimension, maxHeight: gridDimension,
    width: "100%",
    display: "flex", flexDirection: "column", position: "relative"
  } as const;

  return (
    <Paper sx={paperSx}>
      <DataGrid {...buildDataGridProps(props)} />
      <FloatingActions
        rowSelectionModel={grid.rowSelectionModel}
        isJsonCopying={isJsonCopying}
        isRagEnabled={isRagEnabled}
        onOpenLensTester={actions.onOpenLensTester}
        onCopyJson={actions.onCopyJson}
        onToggleChat={actions.onToggleChat}
      />
    </Paper>
  );
};

interface ExploreDialogsProps {
  rows: Artifact[];
  rowSelectionModel: GridRowSelectionModel;
  navigation: UseExploreNavigationResult;
  isChatSidebarOpen: boolean;
  onCloseChatSidebar: () => void;
  isLensTesterOpen: boolean;
  onCloseLensTester: () => void;
  selectedArtifactForTesting: Artifact | null;
  isRankedCallsActive: boolean;
  findBestWorstWizardOpen: boolean;
  onCloseWizard: () => void;
  notification: NotificationState;
  onCloseNotification: (_event?: React.SyntheticEvent | Event, reason?: string) => void;
}

const getSelectedRows = (
  rows: Artifact[],
  rowSelectionModel: GridRowSelectionModel
): { selectedRows: Artifact[]; rowSelectionMode: RowSelectionMode } => {
  const hasSelection = rowSelectionModel.length > INITIAL_INDEX;
  const rowSelectionMode: RowSelectionMode = hasSelection ? "selected" : "all";
  const selectedRows = hasSelection
    ? rows.filter((row: Artifact) => rowSelectionModel.includes(getRowKey(row)))
    : rows;
  return { selectedRows, rowSelectionMode };
};

const ExploreDialogs = (props: ExploreDialogsProps): React.ReactElement => {
  const { selectedRows, rowSelectionMode } = getSelectedRows(props.rows, props.rowSelectionModel);
  const { navigation } = props;

  return (
    <>
      <ViewerDrawer
        isOpen={navigation.isViewerPanelOpen}
        onClose={() => navigation.setIsViewerPanelOpen(false)}
        selectedInteractionId={navigation.selectedInteractionId}
        viewerInitialTab={navigation.viewerInitialTab}
        expandedAttribute={navigation.expandedAttribute}
        currentRowIndex={navigation.currentRowIndex}
        totalCount={props.rows.length}
        onNavigatePrevious={navigation.handleNavigatePrevious}
        onNavigateNext={navigation.handleNavigateNext}
      />
      <ChatSidebar open={props.isChatSidebarOpen} onClose={props.onCloseChatSidebar} selectedRows={selectedRows} rowSelectionMode={rowSelectionMode} />
      <LensTester open={props.isLensTesterOpen} onClose={props.onCloseLensTester} selectedArtifact={props.selectedArtifactForTesting} />
      {props.isRankedCallsActive && (
        <FindBestWorstCallsWizard open={props.findBestWorstWizardOpen} onClose={props.onCloseWizard} />
      )}
      <Snackbar open={props.notification.open} autoHideDuration={SNACKBAR_DURATION} onClose={props.onCloseNotification} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={props.onCloseNotification} severity={props.notification.severity} variant="filled" sx={{ width: "100%" }}>
          {props.notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

interface BuildDataGridPropsParams {
  state: ReturnType<typeof useExploreState>;
  navigation: UseExploreNavigationResult;
  clipboard: ReturnType<typeof useExploreClipboard>;
}

const buildExploreDataGridProps = ({ state, navigation, clipboard }: BuildDataGridPropsParams): ExploreDataGridProps => {
  const { filterApi, rankedCalls, data, grid, ui, isRagEnabled } = state;
  return {
    rankedCalls,
    data,
    grid,
    isFilterBarExpanded: filterApi.isFilterBarExpanded,
    isJsonCopying: clipboard.isJsonCopying,
    isRagEnabled,
    actions: {
      onCellClick: navigation.openInteraction,
      onReopenWizard: navigation.handleReopenWizard,
      onOpenLensTester: state.handleOpenLensTester,
      onCopyJson: clipboard.handleCopyJson,
      onToggleChat: () => ui.setIsChatSidebarOpen(prev => !prev)
    }
  };
};

const buildExploreDialogProps = ({ state, navigation, clipboard }: BuildDataGridPropsParams): ExploreDialogsProps => {
  const { rankedCalls, data, grid, ui } = state;
  return {
    rows: data.rows,
    rowSelectionModel: grid.rowSelectionModel,
    navigation,
    isChatSidebarOpen: ui.isChatSidebarOpen,
    onCloseChatSidebar: () => ui.setIsChatSidebarOpen(false),
    isLensTesterOpen: ui.isLensTesterOpen,
    onCloseLensTester: () => {
      ui.setIsLensTesterOpen(false);
      ui.setSelectedArtifactForTesting(null);
    },
    selectedArtifactForTesting: ui.selectedArtifactForTesting,
    isRankedCallsActive: rankedCalls.isActive,
    findBestWorstWizardOpen: ui.findBestWorstWizardOpen,
    onCloseWizard: () => ui.setFindBestWorstWizardOpen(false),
    notification: clipboard.notification,
    onCloseNotification: clipboard.handleCloseNotification
  };
};

export const Explore = (): React.ReactElement => {
  const state = useExploreState();
  const { filterApi, rankedCalls, data, grid, ui, permittedEmails } = state;

  const navigation = useExploreNavigation({
    artifacts: data.rows,
    rankedCalls,
    setFindBestWorstWizardOpen: ui.setFindBestWorstWizardOpen
  });

  const clipboard = useExploreClipboard({
    artifacts: data.rows,
    rowSelectionModel: grid.rowSelectionModel,
    getRowKey,
    permittedEmails
  });

  useExploreAutoOpen({
    artifacts: data.rows,
    loading: data.loading,
    setSelectedInteractionId: navigation.setSelectedInteractionId,
    setIsViewerPanelOpen: navigation.setIsViewerPanelOpen,
    setViewerInitialTab: navigation.setViewerInitialTab
  });

  return (
    <Box sx={{ p: 1 }}>
      <ExploreHeader
        rankedCallsParams={rankedCalls.rankedCallsParams}
        filters={filterApi.filters}
        rowCount={data.rowCount}
        onFilterChange={filterApi.updateFilter}
        onScoreRangeChange={filterApi.updateScoreRange}
        onRefresh={state.handleRefresh}
        onExpandedChange={filterApi.setIsFilterBarExpanded}
        onReopenWizard={navigation.handleReopenWizard}
      />
      <ExploreDataGrid {...buildExploreDataGridProps({ state, navigation, clipboard })} />
      <ExploreDialogs {...buildExploreDialogProps({ state, navigation, clipboard })} />
    </Box>
  );
};
